import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import TechnologiesByIndustry from '@/components/feature/TechnologiesByIndustry';
import HowWeVetEngineers from '@/components/feature/HowWeVetEngineers';
import { industriesDetail } from '@/mocks/industriesDetail';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const industryData = industriesDetail.find((i) => i.id === 'healthcare')!;

/* ── Trust Badges ── */
function HealthtechTrustBadges() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const badges = [
    { icon: 'ri-hospital-line', label: 'HIPAA Certified', desc: 'Business associate agreements' },
    { icon: 'ri-shield-check-line', label: 'HITRUST CSF', desc: 'Healthcare security framework' },
    { icon: 'ri-lock-star-line', label: 'SOC 2 Type II', desc: 'Audited security controls' },
    { icon: 'ri-file-shield-line', label: 'FDA 21 CFR Part 11', desc: 'Electronic records compliant' },
  ];

  return (
    <section className="py-8 md:py-12 bg-navy-900 border-t border-white/10" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition }}
          className="flex flex-wrap items-center justify-center gap-6 md:gap-10"
        >
          {badges.map((badge, i) => (
            <motion.div
              key={badge.label}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, ...springTransition }}
              className="flex items-center gap-3"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl">
                <i className={`${badge.icon} text-lg text-emerald-400`}></i>
              </div>
              <div>
                <div className="text-sm font-bold text-white">{badge.label}</div>
                <div className="text-xs text-white/50">{badge.desc}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ── Industry FAQ ── */
function HealthtechFAQ() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      q: 'Are your healthtech engineers HIPAA-certified and BAA-ready?',
      a: 'Yes. Every healthtech engineer we place signs a Business Associate Agreement (BAA) and has completed HIPAA training. They understand PHI handling, encryption requirements, minimum necessary access, and audit logging for healthcare data.',
    },
    {
      q: 'Do you have engineers with Epic, Cerner, or Allscripts integration experience?',
      a: 'Yes. We have engineers who have integrated with Epic via FHIR R4 and SMART on FHIR, Cerner PowerChart APIs, and Allscripts Unity APIs. They understand the nuances of clinical data exchange and provider workflow integration.',
    },
    {
      q: 'Can you provide telemedicine platform engineers?',
      a: 'Absolutely. We have full-stack teams who have built HIPAA-compliant video consultation platforms using WebRTC, real-time chat, and remote patient monitoring dashboards. They understand HIPAA-compliant cloud hosting on AWS and Azure.',
    },
    {
      q: 'How do you handle FDA validation for SaMD projects?',
      a: 'We have engineers with ISO 13485 and FDA 21 CFR Part 11 experience who can implement design controls, risk management (ISO 14971), and software validation documentation for Software as a Medical Device (SaMD) projects.',
    },
    {
      q: 'What cloud environments do your healthtech engineers work in?',
      a: 'Our engineers are certified in AWS HIPAA-eligible services, Azure HIPAA/HITRUST environments, and Google Cloud healthcare APIs. They understand BAA requirements with cloud providers and proper PHI isolation.',
    },
  ];

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [faqs]);

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-questionnaire-line text-emerald-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Common Questions</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4"
          >
            Healthtech Hiring
            <span className="text-emerald-600"> FAQ</span>
          </motion.h2>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.08, ...springTransition }}
              className="rounded-2xl border border-gray-100 bg-gray-50 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer group"
              >
                <span className="text-sm md:text-base font-semibold text-navy-900 group-hover:text-emerald-600 transition-colors pr-4">
                  {faq.q}
                </span>
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100 flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-gray-500"></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${openIndex === i ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}
              >
                <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Hero ── */
function PageHero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-20 md:pt-40 md:pb-32 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20healthtech%20background%20with%20glowing%20teal%20medical%20cross%20symbols%20and%20digital%20heart%20pulse%20wave%20lines%20floating%20in%20space%2C%20deep%20navy%20and%20emerald%20teal%20color%20palette%2C%20futuristic%20healthcare%20technology%20aesthetic%2C%20soft%20bokeh%20light%20particles%2C%20professional%20minimal%20composition%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay&width=1600&height=900&seq=healthtech-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-primary-500/10 rounded-full blur-3xl"></div>
      </div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-4xl relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-hospital-line text-emerald-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Industry Focus</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Engineering Talent for
            <br />
            <span className="text-emerald-400">Healthtech & Pharma</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl"
          >
            Build HIPAA-compliant platforms, EHR integrations, and telemedicine infrastructure with engineers who understand FHIR, HL7, and the regulatory landscape of healthcare.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row items-start gap-4"
          >
            <Link
              to="/talent"
              className="px-8 py-4 bg-emerald-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Browse Healthtech Engineers
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/case-studies"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              View Healthtech Case Studies
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ── Healthtech Challenges ── */
function HealthtechChallenges() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const challenges = [
    {
      icon: 'ri-shield-check-line',
      title: 'HIPAA & Regulatory Compliance',
      desc: 'Every line of code must be audit-ready. PHI handling, encryption at rest and in transit, and audit logging are not features — they are requirements.',
      stat: '6 weeks',
      statLabel: 'to HIPAA audit pass',
    },
    {
      icon: 'ri-link-m',
      title: 'Interoperability',
      desc: 'EHRs, lab systems, and payer portals speak different protocols. FHIR, HL7 v2, DICOM — your engineers need to be fluent in healthcare data standards.',
      stat: 'FHIR R4',
      statLabel: 'certified engineers',
    },
    {
      icon: 'ri-video-chat-line',
      title: 'Telemedicine at Scale',
      desc: 'Video consultations, remote patient monitoring, and real-time data streaming require infrastructure that generalist web developers have never built.',
      stat: '99.99%',
      statLabel: 'uptime achieved',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Patient Safety & Reliability',
      desc: 'In healthtech, a bug is not just a bad UX — it can affect patient outcomes. Rigorous testing, fail-safes, and medical-grade reliability are mandatory.',
      stat: 'Zero',
      statLabel: 'critical incidents',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-alert-line text-emerald-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why Healthtech Hiring Is Hard</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            The Healthtech Engineering
            <span className="text-emerald-600"> Challenge</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Healthcare software is not like other software. The stakes are higher, the regulations are stricter, and the domain knowledge gap is wider.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          {challenges.map((c, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${c.icon} text-xl text-white`}></i>
              </div>
              <div className="mb-3">
                <span className="text-2xl md:text-3xl font-bold text-emerald-600">{c.stat}</span>
                <p className="text-xs text-gray-500 mt-0.5">{c.statLabel}</p>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{c.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Solutions ── */
function HealthtechSolutions() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const solutions = [
    {
      title: 'FHIR & EHR Integration',
      desc: 'Engineers who have integrated with Epic, Cerner, and Allscripts. They understand FHIR R4, SMART on FHIR, and the nuances of clinical data exchange.',
      icon: 'ri-hospital-line',
      roles: ['FHIR Engineers', 'EHR Integration Devs', 'HL7 Specialists'],
    },
    {
      title: 'Telemedicine Platforms',
      desc: 'Full-stack teams who build HIPAA-compliant video consultation platforms, remote monitoring dashboards, and patient portals.',
      icon: 'ri-video-chat-line',
      roles: ['React Native Devs', 'WebRTC Engineers', 'Backend Devs'],
    },
    {
      title: 'Clinical Data Pipelines',
      desc: 'Data engineers who build pipelines for clinical trials, lab result processing, and real-time patient data analytics with full audit trails.',
      icon: 'ri-database-2-line',
      roles: ['Data Engineers', 'Python Devs', 'PostgreSQL Experts'],
    },
    {
      title: 'Security & Compliance',
      desc: 'Security engineers who design HIPAA-compliant cloud architectures, implement BAAs, and build automated compliance monitoring.',
      icon: 'ri-shield-keyhole-line',
      roles: ['Security Engineers', 'Cloud Architects', 'DevOps'],
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-user-star-line text-emerald-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">How We Help</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Healthtech Talent,
            <span className="text-emerald-400"> Deployed</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-3xl mx-auto px-2"
          >
            We match engineers who have shipped healthcare code before — not generalists who will learn HIPAA on your budget.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {solutions.map((s, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-white/5 backdrop-blur-md rounded-2xl p-6 md:p-7 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${s.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-2">{s.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed mb-4">{s.desc}</p>
              <div className="flex flex-wrap gap-2">
                {s.roles.map((role) => (
                  <span key={role} className="px-2.5 py-1 bg-white/10 rounded-lg text-xs font-medium text-white/70 border border-white/10">
                    {role}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Case Study ── */
function HealthtechCaseStudy() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-folder-chart-line text-emerald-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Client Success</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Healthtech Case
            <span className="text-emerald-600"> Study</span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, ...springTransition }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center"
        >
          <div className="rounded-2xl overflow-hidden border border-gray-100">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20telemedicine%20platform%20interface%20on%20a%20laptop%20screen%20showing%20video%20consultation%20with%20patient%20vitals%20dashboard%2C%20clean%20white%20and%20teal%20medical%20UI%20design%2C%20HIPAA-compliant%20interface%20mockup%2C%20soft%20ambient%20lighting%2C%20professional%20product%20photography%20style&width=800&height=500&seq=healthtech-cs-1&orientation=landscape"
              alt="Healthtech Platform"
              className="w-full h-64 md:h-80 object-cover"
            />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-bold text-emerald-700">
                Healthcare & Pharma
              </span>
              <span className="text-sm text-gray-400">{industryData.caseStudy.duration}</span>
              <span className="text-sm text-gray-400">{industryData.caseStudy.engineers} engineers</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-3">
              {industryData.caseStudy.client} — {industryData.caseStudy.project}
            </h3>
            <div className="mb-4">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Challenge</span>
              <p className="text-sm text-gray-600 leading-relaxed mt-1">{industryData.caseStudy.challenge}</p>
            </div>
            <div className="mb-6">
              <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Outcome</span>
              <p className="text-sm text-gray-600 leading-relaxed mt-1">{industryData.caseStudy.outcome}</p>
            </div>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer group"
            >
              Read full case study
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Talent Stats ── */
function HealthtechTalentStats() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const stats = [
    { value: industryData.talentPool.totalAvailable.toString(), label: 'Healthtech Engineers Available', icon: 'ri-user-search-line' },
    { value: industryData.talentPool.avgExperience, label: 'Average Experience', icon: 'ri-medal-line' },
    { value: industryData.talentPool.roles.length.toString(), label: 'Role Types', icon: 'ri-group-line' },
    { value: '48h', label: 'Avg. Match Time', icon: 'ri-time-line' },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Available
            <span className="text-emerald-600"> Healthtech Talent</span>
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
          {stats.map((s, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: 0.2 + index * 0.08, ...springTransition }}
              className="bg-white rounded-2xl p-6 md:p-7 border border-gray-100 text-center"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-50 rounded-xl mx-auto mb-3">
                <i className={`${s.icon} text-xl text-emerald-600`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-navy-900 mb-1">{s.value}</div>
              <div className="text-xs md:text-sm text-gray-500">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, ...springTransition }}
          className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8"
        >
          <h3 className="text-lg font-bold text-navy-900 mb-4">Top Healthtech Roles</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {industryData.talentPool.roles.map((role) => (
              <div key={role.title} className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl">
                <span className="text-sm font-medium text-gray-700">{role.title}</span>
                <span className="text-sm font-bold text-navy-900">{role.count}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Top Skills</h4>
            <div className="flex flex-wrap gap-2">
              {industryData.talentPool.topSkills.map((skill) => (
                <span key={skill} className="px-3 py-1.5 bg-emerald-50 border border-emerald-100 rounded-lg text-xs font-semibold text-emerald-700">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── CTA ── */
function HealthtechCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 right-0 w-[400px] h-[400px] bg-emerald-500/15 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 left-0 w-[400px] h-[400px] bg-primary-500/10 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
            <i className="ri-rocket-line text-emerald-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Ready to Heal Healthcare Tech?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4 md:mb-6">
            Hire Healthtech Engineers
            <span className="text-emerald-400"> in 48 Hours</span>
          </h2>
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto mb-8 md:mb-10">
            Get pre-vetted engineers with HIPAA compliance and healthcare interoperability experience. FHIR-certified. HIPAA-aware. Production-ready.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/talent"
              className="px-8 py-4 bg-emerald-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Browse Healthtech Talent
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/how-it-works"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              How It Works
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Page ── */
export default function HealthtechLandingPage() {
  return (
    <PageLayout title="Healthtech Engineering Talent — HIPAA & FHIR Experts" description="Hire pre-vetted healthtech engineers with HIPAA compliance, FHIR R4, HL7, and healthcare interoperability experience. FDA-ready. Deploy in 48 hours." keywords="healthtech engineers for hire, HIPAA developers, FHIR R4 engineers, EHR integration developers, telemedicine platform engineers, healthcare software talent, HITRUST engineers, clinical data pipeline">
      <PageHero />
      <HealthtechTrustBadges />
      <HealthtechChallenges />
      <TechnologiesByIndustry highlightIndustry="healthcare" />
      <HealthtechSolutions />
      <HealthtechCaseStudy />
      <HealthtechTalentStats />
      <HowWeVetEngineers
        accentColor="emerald"
        heading="How We Vet Healthtech Engineers"
        specialtyNote="For healthtech clients, we additionally verify HIPAA training completion, FHIR/HL7 familiarity, and experience working in regulated healthcare environments before any placement."
      />
      <HealthtechFAQ />
      <HealthtechCTA />
    </PageLayout>
  );
}